import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Check, ChevronLeft, ChevronDown, ChevronUp, Star, Mail, User, Phone, Info, Zap, CreditCard, Copy, Building2, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
// EmailJS configuration — sends reservation details to your email when the user confirms
const EMAILJS_SERVICE_ID = 'service_qvmqlhi';
const EMAILJS_TEMPLATE_ID = 'template_7ynesvi';
const EMAILJS_PUBLIC_KEY = '-083vwxCoxw7cbSCz';
// Bank transfer details
const IBAN = 'ES65 2100 0071 1702 0099 5514';
const BENEFICIARY = 'Akram Azghoul Atnibar';
export function CheckoutPage() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMobileSummaryExpanded, setIsMobileSummaryExpanded] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [proofSent, setProofSent] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const handlePay = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Email inválido';
    if (!formData.phone.trim()) newErrors.phone = 'El teléfono es obligatorio';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    // Send reservation details via EmailJS, then show bank transfer instructions
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || 'Sin notas adicionales'
      }, EMAILJS_PUBLIC_KEY);
    } catch (error) {
      console.error('EmailJS error:', error);
      // Continue to instructions even if email fails — user shouldn't lose their reservation flow
    }
    setIsSubmitting(false);
    setIsConfirmed(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(IBAN.replace(/\s/g, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Clipboard error:', e);
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const includedItems = ['Inicio inmediato del proyecto', 'Consulta estratégica personalizada', 'Diseño y desarrollo profesional', 'Soporte prioritario continuo'];
  return <div className="relative min-h-screen bg-[#0a0a0a] font-sans antialiased selection:bg-white selection:text-black" data-id="element-183" data-mp="3b3wm01j7ti5s">
      <div className="flex min-h-screen flex-col lg:flex-row" data-id="element-184" data-mp="a97amx6wyy2z">
        {/* ==================== LEFT PANEL ==================== */}
        <div className="lg:w-[45%] xl:w-[42%] relative bg-[#0a0a0a] lg:border-r border-white/[0.05]" data-id="element-185" data-mp="f8tq7da7sqey">
          <div className="max-w-[440px] ml-auto px-6 md:px-10 lg:pr-16 pt-8 lg:pt-12 pb-8 lg:pb-16 relative z-10" data-id="element-186" data-mp="k8g1viae9yln">
            <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-187" data-mp="t71hp5oheziq">
              {/* Back — Desktop */}
              <motion.button variants={itemVariants} onClick={() => navigate(-1)} className="hidden lg:flex items-center gap-2 text-gray-500 hover:text-gray-300 text-[11px] uppercase tracking-[0.2em] font-medium mb-12 transition-colors" data-id="element-188" data-mp="y3i5tzvoldvt">
                <ChevronLeft size={14} data-id="element-189" data-mp="1csxvfcjh7d9x" />
                Volver
              </motion.button>

              {/* Mobile Header */}
              <div className="flex lg:hidden items-center justify-between mb-8" data-id="element-190" data-mp="31ewlrmpoxwwc">
                <button onClick={() => navigate(-1)} className="flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-white transition-colors" data-id="element-191" data-mp="2xctaay30usb2">
                  <ChevronLeft size={18} data-id="element-192" data-mp="2nggdvsg3e0cm" />
                </button>
                <div className="flex items-center gap-3" data-id="element-193" data-mp="3l1cgn82igv0i">
                  <span className="flex h-8 w-8 items-center justify-center border border-white/20 text-[10px] font-semibold tracking-[-0.08em] text-white" data-id="element-194" data-mp="3g3aw0ipfsnmx">
                    G
                  </span>
                  <span className="text-white font-medium text-sm tracking-wide" data-id="element-195" data-mp="36drdje5myvsl">
                    Growthly
                  </span>
                </div>
                <div className="w-10" data-id="element-196" data-mp="7p2ec5ogdh9j" />
              </div>

              {/* Brand — Desktop */}
              <motion.div variants={itemVariants} className="hidden lg:flex items-center gap-4 mb-12" data-id="element-197" data-mp="2v6jhc3hkail">
                <span className="flex h-12 w-12 items-center justify-center border border-white/20 text-sm font-semibold tracking-[-0.08em] text-white" data-id="element-198" data-mp="215yhzyyzcwen">
                  G
                </span>
                <div data-id="element-199" data-mp="1sdmw8qet9x50">
                  <span className="text-white font-medium text-lg tracking-tight block mb-0.5" data-id="element-200" data-mp="1scuypa1nlpc3">
                    Growthly
                  </span>
                  <span className="text-gray-500 text-[10px] uppercase tracking-[0.2em] font-medium" data-id="element-201" data-mp="1diyt3iwvvask">
                    Digital Ecosystem Agency
                  </span>
                </div>
              </motion.div>

              {/* Price */}
              <motion.div variants={itemVariants} className="mb-8" data-id="element-202" data-mp="19g25fdshkohr">
                <p className="text-gray-500 text-[11px] font-medium uppercase tracking-[0.2em] mb-3" data-id="element-203" data-mp="2yu8c7rsiyfv7">
                  Pago del Proyecto
                </p>
                <div className="flex items-baseline gap-1.5" data-id="element-204" data-mp="33sjosaa7f4h4">
                  <span className="text-5xl font-medium text-white leading-none tracking-tight tabular-nums" data-id="element-205" data-mp="3dw73qjbeoqap">
                    350
                  </span>
                  <span className="text-5xl font-medium text-white/30 leading-none tracking-tight tabular-nums" data-id="element-206" data-mp="38w2iyh3gt9eo">
                    ,00
                  </span>
                  <span className="text-gray-500 text-sm font-medium ml-1 self-end mb-1" data-id="element-207" data-mp="3hxsd3uoqemg5">
                    EUR
                  </span>
                </div>
              </motion.div>

              {/* Mobile Expand */}
              <div className="lg:hidden mb-8" data-id="element-208" data-mp="3rqhkadd91e5o">
                <button onClick={() => setIsMobileSummaryExpanded(!isMobileSummaryExpanded)} className="flex items-center justify-between w-full py-3.5 px-5 rounded-xl bg-white/[0.02] border border-white/[0.05] text-sm text-gray-400 font-medium transition-colors" data-id="element-209" data-mp="9ja67waj1cy4">
                  <span data-id="element-210" data-mp="27amk8187lh68">
                    {isMobileSummaryExpanded ? 'Ocultar detalles' : 'Ver detalles del pedido'}
                  </span>
                  {isMobileSummaryExpanded ? <ChevronUp size={16} className="text-gray-500" data-id="element-211" data-mp="35irpqttn72md" /> : <ChevronDown size={16} className="text-gray-500" data-id="element-212" data-mp="39lej4z5yovlo" />}
                </button>
              </div>

              {/* Expandable Details */}
              <div className={`${!isMobileSummaryExpanded ? 'hidden lg:block' : ''}`} data-id="element-213" data-mp="2cwytl32uqm8k">
                {/* Payment Explanation */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-214" data-mp="2ltqzn2b14mtf">
                  <div className="bg-[#111] border border-white/[0.05] rounded-2xl p-5" data-id="element-215" data-mp="2quncw6h9tlbi">
                    <div className="flex items-center gap-2 mb-4" data-id="element-216" data-mp="15l4kpv6xu79">
                      <div className="w-6 h-6 rounded-full bg-white/[0.05] flex items-center justify-center" data-id="element-217" data-mp="17fnjymwjc8p">
                        <Info size={14} className="text-gray-400" data-id="element-218" data-mp="g839qlwow6uz" />
                      </div>
                      <h3 className="text-white font-medium text-[14px]" data-id="element-219" data-mp="b904p1gpm0n0">
                        ¿Cómo funciona el pago?
                      </h3>
                    </div>

                    <div className="space-y-4" data-id="element-220" data-mp="6ggp5yygovfk">
                      <div className="flex gap-3" data-id="element-221" data-mp="2h7w6wb8x1l2">
                        <CreditCard size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-222" data-mp="2elsx8ve188n" />
                        <div data-id="element-223" data-mp="3omv7sb7odtka">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-224" data-mp="3opzgnlw35dha">
                            Pago único por adelantado
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-225" data-mp="3fojgs82wctrb">
                            Un solo pago de 350€ por transferencia bancaria. Sin
                            cuotas ni cargos posteriores.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-226" data-mp="17v5np4rsidts">
                        <Zap size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-227" data-mp="13vf0tc5dwhuj" />
                        <div data-id="element-228" data-mp="1hu4xwrs7ehiw">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-229" data-mp="1cvtnaeu342zm">
                            Inicio inmediato
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-230" data-mp="p6ilqv4wu4kf">
                            Comenzamos a diseñar tu proyecto en cuanto se
                            confirma la transferencia.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3" data-id="element-231" data-mp="u40w8pyksruj">
                        <ShieldCheck size={16} className="text-gray-500 shrink-0 mt-0.5" data-id="element-232" data-mp="6f7v8f33jtt2" />
                        <div data-id="element-233" data-mp="1etyht6y46ac">
                          <p className="text-gray-300 text-[13px] font-medium mb-0.5" data-id="element-234" data-mp="2d3hzlw8g6tjn">
                            Todo incluido
                          </p>
                          <p className="text-gray-500 text-[12px] leading-relaxed" data-id="element-235" data-mp="2hwtbwcw142iy">
                            Creación, gestión y mantenimiento completos por este
                            único precio.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-236" data-mp="30sb2ydreg3oa" />

                {/* Product card */}
                <motion.div variants={itemVariants} className="mb-8" data-id="element-237" data-mp="2rs4oaojysxnj">
                  <div className="rounded-xl bg-[#111] border border-white/[0.05] overflow-hidden" data-id="element-238" data-mp="1bexrm4kvdfdo">
                    <div className="p-5 flex items-center gap-4" data-id="element-239" data-mp="1gdhgodurf7a9">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/[0.1] bg-[#0a0a0a] text-sm font-semibold tracking-[-0.08em] text-white" data-id="element-240" data-mp="1gbo6iokxu4xi">
                        G
                      </span>
                      <div className="flex-1 min-w-0" data-id="element-241" data-mp="38cbvgicbg7mq">
                        <h3 className="text-white font-medium text-[15px] mb-0.5" data-id="element-242" data-mp="38b0u0p6cfbys">
                          Ecosistema Digital
                        </h3>
                        <p className="text-gray-500 text-[12px]" data-id="element-243" data-mp="2zdglqwa2cfeo">
                          Pago único · Proyecto completo
                        </p>
                      </div>
                      <div className="text-white font-medium text-[15px] tabular-nums" data-id="element-244" data-mp="2pdptl18z6chr">
                        350,00€
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Included */}
                <motion.div variants={itemVariants} className="mb-10" data-id="element-245" data-mp="nks51hm8e62m">
                  <p className="text-[11px] text-gray-500 font-medium mb-4" data-id="element-246" data-mp="jmm0mg8v82kv">
                    Incluido en tu proyecto:
                  </p>
                  <div className="space-y-3" data-id="element-247" data-mp="26i0rfvmpt6vu">
                    {includedItems.map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-248" data-mp="2b8pft233lnqr" data-mpi={i}>
                        <Check size={14} className="shrink-0 text-white" data-id="element-249" data-mp="2batw6ji6of7x" />
                        <span className="text-gray-400 text-[13px]" data-id="element-250" data-mp="2f9136mmui25x">
                          {item}
                        </span>
                      </div>)}
                  </div>
                </motion.div>

                <motion.div variants={itemVariants} className="h-px bg-white/[0.06] mb-8" data-id="element-251" data-mp="1nkzjc1xv6qno" />

                {/* Totals */}
                <motion.div variants={itemVariants} className="space-y-3 mb-10" data-id="element-252" data-mp="1wle3uz3081q4">
                  <div className="flex justify-between text-[13px]" data-id="element-253" data-mp="32z3p0l062bw3">
                    <span className="text-gray-500" data-id="element-254" data-mp="37ygms0skt3dg">Subtotal</span>
                    <span className="text-gray-300 tabular-nums" data-id="element-255" data-mp="380t3kamll7ox">350,00€</span>
                  </div>
                  <div className="flex justify-between text-[13px]" data-id="element-256" data-mp="36jno4uixl95q">
                    <span className="text-gray-500" data-id="element-257" data-mp="31kathnltixsl">IVA</span>
                    <span className="text-gray-300" data-id="element-258" data-mp="3fj0rr6kdj3ij">Incluido</span>
                  </div>
                  <div className="pt-3 mt-3 border-t border-white/[0.06] flex justify-between items-center" data-id="element-259" data-mp="3bh5rm5e6iw6c">
                    <span className="text-white font-medium text-[15px]" data-id="element-260" data-mp="3pfwzvb6d1nmk">
                      Total a pagar
                    </span>
                    <div className="flex items-baseline gap-1" data-id="element-261" data-mp="3kh2ga70kjv1e">
                      <span className="text-white font-medium text-xl tabular-nums" data-id="element-262" data-mp="31dnfk0y4iiq">
                        350,00
                      </span>
                      <span className="text-gray-400 text-sm" data-id="element-263" data-mp="3u9i1dd75wzqi">€</span>
                    </div>
                  </div>
                </motion.div>

                {/* Social proof */}
                <motion.div variants={itemVariants} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[#111] border border-white/[0.05]" data-id="element-264" data-mp="74abvrd638ul">
                  <div className="flex gap-0.5" data-id="element-265" data-mp="vn2a0p32b2j6">
                    {[...Array(5)].map((_, i) => <Star key={i} size={12} className="fill-white text-white" data-id="element-266" data-mp="hnjceq7vn9l4" data-mpi={i} />)}
                  </div>
                  <div className="text-gray-400 text-[12px]" data-id="element-267" data-mp="7w6kxzpzqlfm">
                    <span className="text-white font-medium" data-id="element-268" data-mp="cphu47i9zeap">+4.000</span>{' '}
                    proyectos completados
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ==================== RIGHT PANEL ==================== */}
        <div className="w-full lg:w-[55%] xl:w-[58%] bg-[#0a0a0a] min-h-screen relative" data-id="element-269" data-mp="3l5bammclwprr">
          <div className="max-w-[560px] mx-auto lg:mx-0 lg:mr-auto px-6 md:px-10 lg:pl-16 pt-12 lg:pt-20 pb-36 lg:pb-24 relative z-10" data-id="element-270" data-mp="3q333pjozmhxj">
            {!isConfirmed ? <motion.div variants={containerVariants} initial="hidden" animate="show" data-id="element-271" data-mp="1qwfjifpjsiuh">
                {/* Form Container */}
                <div className="bg-[#111] border border-white/[0.05] rounded-3xl p-6 md:p-10" data-id="element-272" data-mp="1hwj0oupuae7c">
                  {/* Section 01 */}
                  <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-273" data-mp="2elqdnece7ezn">
                    <span className="text-[28px] font-light text-gray-600 leading-none" data-id="element-274" data-mp="25lbtwjf2u7y9">
                      01
                    </span>
                    <div data-id="element-275" data-mp="20lz53t4naqt1">
                      <h2 className="text-white font-medium text-lg" data-id="element-276" data-mp="20olezzf0dief">
                        Contacto
                      </h2>
                      <p className="text-gray-500 text-[13px] mt-1" data-id="element-277" data-mp="p6smklo7jcb8">
                        Para enviarte la confirmación del proyecto
                      </p>
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div variants={itemVariants} className="mb-8" data-id="element-278" data-mp="35u0bkunv5lfj">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-279" data-mp="39uaraz1gjq7l">
                      Correo electrónico
                    </label>
                    <div className="relative" data-id="element-280" data-mp="3jlycwjbc2d15">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-281" data-mp="3jn9fwgwyfgr7">
                        <Mail size={16} className={`transition-colors duration-300 ${focusedField === 'email' ? 'text-white' : 'text-gray-600'}`} data-id="element-282" data-mp="3om2wwjbg9pw4" />
                      </div>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.email ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] transition-all duration-300 placeholder:text-gray-700 focus:outline-none focus:border-white/60 focus-visible:ring-2 focus-visible:ring-white/70`} placeholder="tu@correo.com" data-id="element-283" data-mp="1z6d561et8zak" />
                    </div>
                    {errors.email && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-284" data-mp="1adjtqc65o1cs">
                        {errors.email}
                      </p>}
                  </motion.div>

                  <motion.div variants={itemVariants} className="h-px bg-white/[0.06] my-8" data-id="element-285" data-mp="2lquor3k104a9" />

                  {/* Section 02 */}
                  <motion.div variants={itemVariants} className="mb-8 flex items-baseline gap-4" data-id="element-286" data-mp="2p91u82no72r0">
                    <span className="text-[28px] font-light text-gray-600 leading-none" data-id="element-287" data-mp="2t92azty8rq18">
                      02
                    </span>
                    <div data-id="element-288" data-mp="2k9ytgueq3iou">
                      <h2 className="text-white font-medium text-lg" data-id="element-289" data-mp="2kb9vaoolygdb">
                        Datos personales
                      </h2>
                    </div>
                  </motion.div>

                  {/* Name + Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-290" data-mp="3d0yx34956kfz">
                    <motion.div variants={itemVariants} data-id="element-291" data-mp="2y9zs037ime15">
                      <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-292" data-mp="338kt04uld1qn">
                        Nombre completo
                      </label>
                      <div className="relative" data-id="element-293" data-mp="3vq5kh4kvs084">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-294" data-mp="3vqyi9g1v3fe4">
                          <User size={16} className={`transition-colors duration-300 ${focusedField === 'name' ? 'text-white' : 'text-gray-600'}`} data-id="element-295" data-mp="1r7ghmq1hpzqt" />
                        </div>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} onFocus={() => setFocusedField('name')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.name ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] transition-all duration-300 placeholder:text-gray-700 focus:outline-none focus:border-white/60 focus-visible:ring-2 focus-visible:ring-white/70`} placeholder="Juan Pérez" data-id="element-296" data-mp="1mdcc8b7vhzru" />
                      </div>
                      {errors.name && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-297" data-mp="3307g9ppburvy">
                          {errors.name}
                        </p>}
                    </motion.div>

                    <motion.div variants={itemVariants} data-id="element-298" data-mp="3bxrk96ekr2a9">
                      <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-299" data-mp="3qnpbxwbpqa47">
                        Teléfono
                      </label>
                      <div className="relative" data-id="element-300" data-mp="3jizulm7hht6">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-301" data-mp="3ize0ttfq99v">
                          <Phone size={16} className={`transition-colors duration-300 ${focusedField === 'phone' ? 'text-white' : 'text-gray-600'}`} data-id="element-302" data-mp="3vo392jbrzzng" />
                        </div>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)} className={`w-full bg-[#0a0a0a] border ${errors.phone ? 'border-red-500/40' : 'border-white/[0.08]'} rounded-xl pl-11 pr-4 py-3.5 text-white text-[15px] transition-all duration-300 placeholder:text-gray-700 focus:outline-none focus:border-white/60 focus-visible:ring-2 focus-visible:ring-white/70`} placeholder="+34 600 000 000" data-id="element-303" data-mp="49g2pi2y2uvx" />
                      </div>
                      {errors.phone && <p className="text-red-400/90 text-[12px] mt-2 ml-1" data-id="element-304" data-mp="16wswiftp9sd2">
                          {errors.phone}
                        </p>}
                    </motion.div>
                  </div>

                  {/* Message */}
                  <motion.div variants={itemVariants} className="mb-8" data-id="element-305" data-mp="2rdscoatkqeop">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-2 ml-1" data-id="element-306" data-mp="2rfwrhnt0gy3r">
                      Notas{' '}
                      <span className="text-gray-600 normal-case tracking-normal" data-id="element-307" data-mp="2hge8paou7ida">
                        (opcional)
                      </span>
                    </label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} onFocus={() => setFocusedField('message')} onBlur={() => setFocusedField(null)} rows={3} className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-[15px] transition-all duration-300 resize-none placeholder:text-gray-700 focus:outline-none focus:border-white/60 focus-visible:ring-2 focus-visible:ring-white/70" placeholder="Detalles sobre tu proyecto..." data-id="element-308" data-mp="3a3i72wtfcyg3" />
                  </motion.div>

                  {/* Confirm Button */}
                  <motion.div variants={itemVariants} data-id="element-309" data-mp="33ot8euhhogrr">
                    <button onClick={handlePay} disabled={isSubmitting} className={`flex w-full items-center justify-center gap-2.5 rounded-xl px-6 py-[18px] text-[15px] font-medium transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#111] ${isSubmitting ? 'cursor-wait bg-white/30 text-white/50' : 'bg-white text-[#0a0a0a] hover:bg-neutral-200'}`} data-id="element-310" data-mp="38mupj9vcu60l">
                      {isSubmitting ? <div className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full animate-spin" data-id="element-311" data-mp="nvas3p6ur46z" /> : <>
                          <Lock size={16} data-id="element-312" data-mp="23mwh1upa9j5u" />
                          <span data-id="element-313" data-mp="23ltt9vn9bwu5">Confirmar pago · 350,00€</span>
                        </>}
                    </button>
                  </motion.div>

                  {/* Transfer note */}
                  <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 mt-5" data-id="element-314" data-mp="2ddzjp0xv2678">
                    <Building2 size={12} className="text-gray-600" data-id="element-315" data-mp="1kosj7weuj6jc" />
                    <p className="text-gray-500 text-[12px]" data-id="element-316" data-mp="1yns7v42czlfi">
                      Pago por transferencia bancaria · Datos al confirmar
                    </p>
                  </motion.div>
                </div>

                {/* Trust row */}
                <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center gap-6 text-[12px] text-gray-500" data-id="element-317" data-mp="3a1xb1uuqzr9s">
                  <div className="flex items-center gap-1.5" data-id="element-318" data-mp="22r96nlbxqx6c">
                    <ShieldCheck size={14} className="text-gray-400" data-id="element-319" data-mp="27pj09ri0hppu" />
                    <span data-id="element-320" data-mp="27npt8b3hl3hb">Datos protegidos</span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-321" data-mp="2bs775tn60mk6">
                    <Check size={14} className="text-gray-400" data-id="element-322" data-mp="2gpz1svf72ms2" />
                    <span data-id="element-323" data-mp="2gpfr9zrvj0o2">Confirmación inmediata</span>
                  </div>
                </motion.div>

                {/* Direct contact - builds trust before payment */}
                <motion.div variants={itemVariants} className="mt-6 p-5 rounded-2xl bg-[#111] border border-white/[0.05]" data-id="element-324" data-mp="1o2d0jttfvu8d">
                  <p className="text-center text-gray-400 text-[13px] mb-4" data-id="element-325" data-mp="1xrmqb30t90tb">
                    ¿Tienes dudas antes de empezar? Estamos disponibles.
                  </p>
                  <div className="grid grid-cols-2 gap-3" data-id="element-326" data-mp="15b29ncnprhgh">
                    <a href="tel:+34631012592" className="group flex items-center justify-center gap-2 rounded-xl border border-white/[0.05] bg-white/[0.03] px-4 py-3 transition-all duration-300 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#111]" data-id="element-327" data-mp="159hfvp9842jo">
                      <Phone size={14} className="text-white" data-id="element-328" data-mp="jri5446v7i0g" />
                      <span className="text-white text-[13px] font-medium transition-colors" data-id="element-329" data-mp="etqgbj1ioc0h">
                        Llamar
                      </span>
                    </a>
                    <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.06] transition-all duration-300 group" data-id="element-330" data-mp="52cayumvwhzf">
                      <svg className="w-3.5 h-3.5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" data-id="element-331" data-mp="1ezslu9fr75mv">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-332" data-mp="15z471cijd18x" />
                      </svg>
                      <span className="text-white text-[13px] font-medium transition-colors" data-id="element-333" data-mp="3ljanz4z12of">
                        WhatsApp
                      </span>
                    </a>
                  </div>
                  <p className="text-center text-gray-600 text-[11px] mt-3 font-medium" data-id="element-334" data-mp="cn9398ala7ns">
                    +34 631 01 25 92
                  </p>
                </motion.div>
              </motion.div> /* ==================== CONFIRMATION / TRANSFER INSTRUCTIONS ==================== */ : <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} data-id="element-335" data-mp="10b0pp4pug12s">
                {/* Success header */}
                <div className="flex items-center gap-3 mb-8" data-id="element-336" data-mp="1ntu10n2h1tes">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10" data-id="element-337" data-mp="1iseesrok5gut">
                    <CheckCircle2 size={22} className="text-white" data-id="element-338" data-mp="1esnh040m60gs" />
                  </div>
                  <div data-id="element-339" data-mp="19z1aktl1mstf">
                    <h2 className="text-white font-medium text-xl tracking-tight" data-id="element-340" data-mp="19yrjt3s6chmo">
                      Solicitud registrada
                    </h2>
                    <p className="text-gray-500 text-[13px] mt-0.5" data-id="element-341" data-mp="103hiyjdiigah">
                      Completa la transferencia para activar tu proyecto.
                    </p>
                  </div>
                </div>

                {/* Transfer details card */}
                <div className="bg-[#111] border border-white/[0.08] rounded-2xl p-6 md:p-8 mb-6 shadow-sm" data-id="element-342" data-mp="32p3o8kjybnha">
                  <p className="text-[11px] text-gray-400 uppercase tracking-[0.1em] font-medium mb-6" data-id="element-343" data-mp="36qf6u64er8fz">
                    Datos para la transferencia
                  </p>

                  <div className="space-y-0" data-id="element-344" data-mp="3bnyvlzyqf3m6">
                    {/* IBAN */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-3" data-id="element-345" data-mp="3go3c3pzn3yce">
                      <span className="text-gray-500 text-[13px]" data-id="element-346" data-mp="2nzxrd38l69ck">IBAN</span>
                      <div className="flex items-center gap-3" data-id="element-347" data-mp="2nxurzsljn3v6">
                        <code className="text-white text-[14px] tracking-wide tabular-nums font-medium" data-id="element-348" data-mp="2syijefdopmpx">
                          {IBAN}
                        </code>
                        <button onClick={copyIban} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/[0.05] bg-white/[0.04] text-gray-400 transition-colors hover:bg-white/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" title="Copiar IBAN" data-id="element-349" data-mp="20b4t304hrpes">
                          {copied ? <Check size={14} className="text-white" data-id="element-350" data-mp="270nv4bd7q61f" /> : <Copy size={14} data-id="element-351" data-mp="2222y0kt9c2h7" />}
                        </button>
                      </div>
                    </div>

                    {/* Bank */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-352" data-mp="1aax2khohesy9">
                      <span className="text-gray-500 text-[13px]" data-id="element-353" data-mp="1j8pfxgan7qj0">Banco</span>
                      <span className="text-white text-[14px] font-medium" data-id="element-354" data-mp="2znid91mk4tbq">
                        Caixabank
                      </span>
                    </div>

                    {/* Beneficiary */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-355" data-mp="ggwhenu4ilr6">
                      <span className="text-gray-500 text-[13px]" data-id="element-356" data-mp="lgiumx9yc1zz">
                        Beneficiario
                      </span>
                      <span className="text-white text-[14px] font-medium" data-id="element-357" data-mp="7i1crg6q9jpz">
                        {BENEFICIARY}
                      </span>
                    </div>

                    {/* Concept — fixed transfer reference */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-358" data-mp="145z5adfcpau4">
                      <span className="text-gray-500 text-[13px]" data-id="element-359" data-mp="qe16owj9mxc7">
                        Concepto
                      </span>
                      <span className="text-white text-[14px] font-medium" data-id="element-360" data-mp="vbsvvisj05cp">
                        Pago Growthly
                      </span>
                    </div>

                    {/* Representative full name + contact number */}
                    <div className="flex flex-col md:flex-row md:items-start justify-between py-4 border-b border-white/[0.05] gap-2" data-id="element-361" data-mp="3veayt5j9tpqt">
                      <span className="text-gray-500 text-[13px] md:pt-0.5" data-id="element-362" data-mp="3qd53czy87odl">
                        Representante
                      </span>
                      <div className="flex flex-col md:items-end gap-1" data-id="element-363" data-mp="3he07k1e1u954">
                        <span className="text-white text-[14px] font-medium" data-id="element-364" data-mp="3hg35r8pcv8kx">
                          {formData.name.trim() || 'Indicado en tu solicitud'}
                        </span>
                        {formData.phone.trim() && <span className="text-gray-400 text-[13px] tabular-nums" data-id="element-365" data-mp="961hzxnfaucj">
                            {formData.phone.trim()}
                          </span>}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between py-4 gap-2" data-id="element-366" data-mp="ww5ku2sseq2h">
                      <span className="text-gray-500 text-[13px]" data-id="element-367" data-mp="147k3nm1i2u3l">Importe</span>
                      <span className="text-white text-[16px] font-medium tabular-nums" data-id="element-368" data-mp="149mx6g1v35d2">
                        350,00€
                      </span>
                    </div>
                  </div>
                </div>

                {/* Send proof */}
                <div className="bg-[#111] border border-white/[0.08] rounded-2xl p-6 mb-6" data-id="element-369" data-mp="1i7ureg7ijskj">
                  {!proofSent ? <>
                      <p className="text-gray-400 text-[13px] leading-relaxed mb-5" data-id="element-370" data-mp="1s4zhgr4hc86m">
                        Una vez realizada la transferencia, envía el comprobante
                        de pago por WhatsApp a{' '}
                        <span className="text-gray-300 font-medium" data-id="element-371" data-mp="21vbzabkwtn92">
                          +34 631 01 25 92
                        </span>{' '}
                        para activar tu proyecto de inmediato.
                      </p>
                      <div className="flex flex-col gap-3" data-id="element-372" data-mp="2awa2wsrfrsr4">
                        <a href="https://wa.me/34631012592" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2.5 w-full py-3.5 px-4 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] transition-colors" data-id="element-373" data-mp="aszfiu00irna">
                          <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" data-id="element-374" data-mp="ugyrg86bc4ua">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-375" data-mp="13h3n5l259esm" />
                          </svg>
                          <span className="text-white text-[13px] font-medium" data-id="element-376" data-mp="2iepx090bu7ei">
                            Enviar comprobante por WhatsApp
                          </span>
                        </a>
                        <button onClick={() => setProofSent(true)} className="w-full py-3.5 px-4 rounded-xl bg-transparent border border-white/[0.05] text-gray-400 hover:text-white hover:bg-white/[0.02] text-[13px] font-medium transition-colors" data-id="element-377" data-mp="1a80tepagz1o6">
                          Ya lo he enviado
                        </button>
                      </div>
                    </> : <div className="flex flex-col items-center justify-center text-center py-4" data-id="element-378" data-mp="mhwrqngudbzt">
                      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10" data-id="element-379" data-mp="hitx7wyhmo63">
                        <CheckCircle2 size={20} className="text-white" data-id="element-380" data-mp="cjz5g5ijlp9u" />
                      </div>
                      <h3 className="text-white font-medium text-[15px] mb-1.5" data-id="element-381" data-mp="2bwl2kk9salvq">
                        ¡Gracias por confirmar!
                      </h3>
                      <p className="text-gray-500 text-[13px] leading-relaxed max-w-[280px]" data-id="element-382" data-mp="26yjjw0g9soks">
                        Revisaremos el comprobante y activaremos tu proyecto en
                        breve.
                      </p>
                    </div>}
                </div>

                <button onClick={() => navigate('/')} className="w-full flex justify-center items-center gap-2 px-6 py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.05] text-gray-300 hover:bg-white/[0.06] text-[14px] font-medium transition-colors" data-id="element-383" data-mp="2o7ujar0owfre">
                  Volver al inicio
                </button>
              </motion.div>}
          </div>

          {/* Mobile sticky button — only during form step */}
          {!isConfirmed && <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50" data-id="element-384" data-mp="2adbc829swo2r">
              <div className="h-6 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none" data-id="element-385" data-mp="3gv8bkbtg0n56" />
              <div className="border-t border-white/[0.06] bg-[#0a0a0a] p-4 pb-8" data-id="element-386" data-mp="3lt00cwysjt55">
                <button onClick={handlePay} disabled={isSubmitting} className={`flex w-full items-center justify-center gap-2.5 rounded-xl px-6 py-[18px] text-[15px] font-medium transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] ${isSubmitting ? 'cursor-wait bg-white/30 text-white/50' : 'bg-white text-[#0a0a0a]'}`} data-id="element-387" data-mp="3j8t3d9j4jzhx">
                  {isSubmitting ? <div className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full animate-spin" data-id="element-388" data-mp="a06xpus5twxf" /> : <>
                      <Lock size={16} data-id="element-389" data-mp="jruxciyvek9z" />
                      <span data-id="element-390" data-mp="orfx2vlv6wnr">Confirmar pago · 350,00€</span>
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}